/* LanguageUrlSync — keeps the URL's ?lang= query param in sync with the
 * active language across in-app navigations.
 *
 * Why we need it: react-router's <Link> navigations clear the previous
 * query string, so without this component a visitor browsing in Spanish
 * who clicks "About" would land on /about (no lang param). That URL is no
 * longer copy-shareable in Spanish. This component re-applies ?lang=xx
 * after every navigation on public routes.
 *
 * It deliberately skips admin/auth routes to keep those URLs clean.
 */
import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useLanguage, SUPPORTED_LANGUAGES } from '../contexts/LanguageContext';

const SUPPORTED_CODES = new Set(SUPPORTED_LANGUAGES.map((l) => l.code));
const SKIP_PREFIXES = ['/admin', '/auth'];
const DEFAULT_LANG = 'en';

const LanguageUrlSync = () => {
  const { lang, setLang } = useLanguage();
  const location = useLocation();

  // Hard cases:
  //  1. Visitor lands on /donate?lang=es while their localStorage still says
  //     en — the URL must win and flip the active language immediately.
  //  2. Visitor in es clicks <Link to="/about"> — react-router replaces the
  //     URL with /about (no query). We need to re-append ?lang=es.
  //  3. Active language is en — URL must NOT carry ?lang=en (kept clean).
  useEffect(() => {
    if (SKIP_PREFIXES.some((p) => location.pathname.startsWith(p))) return;
    if (typeof window === 'undefined') return;

    try {
      const url = new URL(window.location.href);
      const urlLang = (url.searchParams.get('lang') || '').toLowerCase();

      // Case 1 — URL has a valid lang that differs from active state → adopt it.
      if (urlLang && SUPPORTED_CODES.has(urlLang) && urlLang !== lang) {
        setLang(urlLang);
        return;
      }

      // Case 2 / 3 — reconcile URL to match active language.
      let dirty = false;
      if (lang === DEFAULT_LANG) {
        if (url.searchParams.has('lang')) {
          url.searchParams.delete('lang');
          dirty = true;
        }
      } else if (url.searchParams.get('lang') !== lang) {
        url.searchParams.set('lang', lang);
        dirty = true;
      }
      if (dirty) {
        const next = url.pathname + (url.search ? url.search : '') + (url.hash || '');
        window.history.replaceState(window.history.state, '', next);
      }
    } catch { /* ignore — URL ops can fail in old browsers */ }
  }, [location.pathname, location.search, lang, setLang]);

  return null;
};

export default LanguageUrlSync;
